import { Link, redirect, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { claimConnection } from '@/lib/api/connections';
import { TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import { requireAuth } from '@/lib/auth/auth-guard';
import { queryClient } from '@/lib/query/client';

/**
 * `/app/bc-claim` — terminal step of the BC-initiated install flow
 * (ADR 0011). The merchant arrives here from `/signup?bc_claim=…` or
 * `/signin?next=/app/bc-claim?id=…` carrying the unattached pending
 * Connection id; this route attaches that connection to the tenant
 * and bounces to `/app` (where `firstIncompleteStep` then routes to
 * the right wizard step — usually step 4 since BC is now done).
 *
 * The whole claim happens in the loader so the user doesn't see a
 * flash of "claiming…" UI on the happy path. The page component
 * renders only on failure, with a Retry CTA and a back-to-dashboard
 * link.
 */

interface BcClaimLoaderError {
  readonly kind: 'missing_id' | 'not_claimable' | 'server_error' | 'unauthorised';
  readonly message: string;
}

export async function bcClaimLoader(
  args: LoaderFunctionArgs,
): Promise<Response | BcClaimLoaderError> {
  const authCheck = requireAuth(args);
  if (authCheck) return authCheck;

  const url = new URL(args.request.url);
  const connectionId = url.searchParams.get('id');
  if (!connectionId) {
    // Nothing to claim — likely a stray visit. Bounce silently.
    return redirect('/app');
  }

  const result = await claimConnection({ connection_id: connectionId });
  if (result.isErr()) {
    if (result.error.kind === 'unauthorised') {
      return redirect('/signin');
    }
    if (result.error.kind === 'not_found') {
      return {
        kind: 'not_claimable',
        message:
          'That BigCommerce install link is no longer valid. It may have been claimed by a different account, expired, or never completed. Re-install Mailchute from your BigCommerce store admin to try again.',
      };
    }
    return {
      kind: 'server_error',
      message: result.error.message,
    };
  }

  // Claim succeeded — the tenant's BC connection state has changed,
  // so invalidate the cached `tenant/me` query before bouncing into
  // the app so the next loader hits a fresh response.
  await queryClient.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
  return redirect('/app');
}

export function BcClaimPage() {
  // The loader returns a Response on every happy-path branch (or a
  // 401 → /signin redirect) — by the time this component mounts the
  // only remaining possibility is the error object, so we type
  // useLoaderData directly with that.
  const error = useLoaderData<BcClaimLoaderError>();

  return (
    <div className="bg-muted/30 flex min-h-screen items-center justify-center px-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Couldn&apos;t link your BigCommerce store</CardTitle>
          <CardDescription>
            We got your install but couldn&apos;t attach it to your Mailchute account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
          <div className="flex justify-end">
            <Button asChild>
              <Link to="/app">Go to dashboard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
